<template>
  <div :class="classes" :qa-locator-id="locators.set()">
    <svg class="sprite" :style="styles">
      <use class="settle" :href="`${iconPath}#${icon}`" />
      <use v-if="iconHover" class="hover" :href="`${iconPath}#${iconHover}`" />
    </svg>
    <div v-if="dot" class="dot" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { useQaLocators } from '~/composables/use-qa-locators';

const COMPONENT_CLASS_NAME: string = 'icon';
const V2_ICON_PATH = '/v2/icons/icons.svg';
const V3_ICON_PATH = '/v3/icons/icons_v.3.22.svg';
const BLACK_ICON_PATH = '/v3/icons/black.svg';

const locators = useQaLocators(COMPONENT_CLASS_NAME);

const props = defineProps({
  collection: {
    type: String,
    validator: (value: string) => ['v2', 'v3', 'black'].includes(value),
    default: 'v3',
  },
  icon: {
    type: String,
    default: '',
  },
  iconHover: {
    type: String,
    default: '',
  },
  size: {
    type: String,
    // validator: (value: string) => !value || ['l', 'm', 's'].includes(value),
    default: 'l',
  },
  dot: {
    type: Boolean,
    default: false,
  },
  rotate: {
    type: [Number, String],
    default: 0,
  },
});

const iconPath = computed(() => {
  switch (props.collection) {
    case 'v2':
      return V2_ICON_PATH;
    case 'v3':
      return V3_ICON_PATH;
    case 'black':
      return BLACK_ICON_PATH;
    default:
      return V3_ICON_PATH;
  }
});

const classes = computed(() => {
  return {
    [COMPONENT_CLASS_NAME]: true,
    [`size-${props.size}`]: true,
    hovered: props.iconHover,
    dot: props.dot,
  };
});

const styles = computed(() => {
  return {
    transform: `rotate(${props.rotate}deg)`,
  };
});
</script>

<style lang="scss" scoped>
.icon {
  position: relative;
  display: flex;

  &.size-xxs {
    > .sprite {
      width: 8px;
      height: 8px;
    }
  }
  &.size-xs {
    > .sprite {
      width: 12px;
      height: 12px;
    }
  }
  &.size-s {
    > .sprite {
      width: 16px;
      height: 16px;
    }
  }
  &.size-m {
    > .sprite {
      width: 20px;
      height: 20px;
    }
  }
  &.size-l {
    > .sprite {
      width: 24px;
      height: 24px;
    }
  }
  &.size-xl {
    > .sprite {
      width: 32px;
      height: 32px;
    }
  }
  &.size-full {
    > .sprite {
      width: 100%;
      height: 100%;
    }
  }

  > .dot {
    position: absolute;
    top: -3px;
    right: -3px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: $color-chilli-50;
    border: 2px solid #fff;
  }

  > .sprite > .hover {
    display: none;
  }

  @media (hover: hover) {
    &.hovered:hover {
      > .sprite > .settle {
        display: none;
      }
      > .sprite > .hover {
        display: unset;
      }
    }
  }
}
</style>
